import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { trackButtonClick } from "@/lib/metaPixel";
import { trackGAButtonClick } from "@/lib/googleAnalytics";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-transparent text-foreground hover:bg-secondary hover:border-primary",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-primary text-primary-foreground font-semibold uppercase tracking-wider hover:bg-primary/90 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all duration-300",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-md px-12 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  trackClick?: boolean | string; // Se false, non traccia; se true, usa il testo del bottone; se string, usa quella stringa
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, trackClick = true, onClick, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Traccia il click automaticamente (a meno che trackClick sia false)
      if (trackClick !== false) {
        let buttonName = 'Button';
        
        if (typeof trackClick === 'string') {
          buttonName = trackClick;
        } else if (typeof children === 'string') {
          buttonName = children.trim();
        } else if (React.isValidElement(children) && typeof children.props.children === 'string') {
          buttonName = children.props.children.trim();
        } else if (props['aria-label']) {
          buttonName = props['aria-label'];
        }
        
        const trackingParams = {
          variant: variant || undefined,
          size: size || undefined,
          href: 'href' in props ? String(props.href) : undefined,
        };
        
        // Traccia su entrambe le piattaforme
        trackButtonClick(buttonName, trackingParams);
        trackGAButtonClick(buttonName, trackingParams);
      }
      
      // Chiama l'onClick originale se presente
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        onClick={handleClick}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
